import React from 'react';
import { Box, IconButton, Tooltip, Fab, SvgIcon } from '@mui/material';
import { Circle } from '@mui/icons-material';
import { useConversation } from '../contexts/ConversationContext';
import { useAppConfig } from '../contexts/ConfigContext';

// Custom EditSquare icon component using the SVG file
const EditSquareIcon: React.FC<{ fontSize?: 'small' | 'medium' | 'large' }> = ({ fontSize = 'medium' }) => {
  return (
    <SvgIcon fontSize={fontSize}>
      <path d="M20,11.5 C20,11.2238576 20.2238576,11 20.5,11 C20.7761424,11 21,11.2238576 21,11.5 L21,18.5000057 C21,19.8807175 19.8807119,21.0000057 18.5,21.0000057 L5.48612181,21.0000057 C4.10540994,21.0000057 2.98612181,19.8807175 2.98612181,18.5000057 L2.98612181,5.5 C2.98612181,4.11928813 4.10540994,3 5.48612181,3 L12.5,3 C12.7761424,3 13,3.22385763 13,3.5 C13,3.77614237 12.7761424,4 12.5,4 L5.48612181,4 C4.65769469,4 3.98612181,4.67157288 3.98612181,5.5 L3.98612181,18.5000057 C3.98612181,19.3284328 4.65769469,20.0000057 5.48612181,20.0000057 L18.5,20.0000057 C19.3284271,20.0000057 20,19.3284328 20,18.5000057 L20,11.5 Z M18.8535534,3.14644661 L20.8535534,5.14644661 C21.0488155,5.34170876 21.0488155,5.65829124 20.8535534,5.85355339 L12.8535534,13.8535534 C12.7597852,13.9473216 12.6326082,14 12.5,14 L10.5,14 C10.2238576,14 10,13.7761424 10,13.5 L10,11.5 C10,11.3673918 10.0526784,11.2402148 10.1464466,11.1464466 L18.1464466,3.14644661 C18.3417088,2.95118446 18.6582912,2.95118446 18.8535534,3.14644661 Z M18.5,4.20710678 L11,11.7071068 L11,13 L12.2928932,13 L19.7928932,5.5 L18.5,4.20710678 Z"/>
    </SvgIcon>
  );
};

const ConnectionStatus: React.FC = () => {
  const { connectionStatus, connect, startNewConversation, messages, isProcessing } = useConversation();
  const { config } = useAppConfig();

  // Check if any message is currently streaming
  const isStreaming = messages.some(msg => msg.isStreaming === true);
  
  // Button should be disabled if: no messages, processing, or streaming
  const isButtonDisabled = messages.length === 0 || isProcessing || isStreaming;

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return '#4caf50'; // green
      case 'connecting':
        return '#ff9800'; // orange
      case 'disconnected':
      default:
        return '#f44336'; // red
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
      default:
        return 'Disconnected';
    }
  };

  const handleClick = () => {
    if (connectionStatus === 'disconnected') {
      connect();
    }
  };  return (
    <Box className="status-indicator" sx={{ 
      display: 'grid', 
      gridTemplateColumns: '28px 1fr 28px', 
      alignItems: 'center', 
      px: 0.25,
      py: 0,
      minHeight: 32
    }}>
      {/* New Conversation Button - Left Side */}
      <Tooltip title={config?.ui_tooltips?.new_conversation_tooltip || "Start a new conversation"}>
        <span>
          <Fab
            onClick={startNewConversation}
            disabled={isButtonDisabled}
            size="small"
            sx={{
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: config?.ui_theme?.new_conversation_button_background || '#1976d2',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: `${config?.ui_theme?.new_conversation_button_hover_background || '#1565c0'} !important`,
              },
              '&.Mui-disabled': {
                backgroundColor: '#cccccc',
                color: '#666666',
              },
            }}
          >
            <EditSquareIcon fontSize="small" />
          </Fab>
        </span>
      </Tooltip>

      {/* Connection Status - Center (conditionally rendered) */}
      {config?.ui_behavior?.show_connection_status ? (
        <Tooltip title={getStatusText()}>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={connectionStatus !== 'disconnected'}
            sx={{
              p: 0.25,
              cursor: connectionStatus === 'disconnected' ? 'pointer' : 'default',
              justifySelf: 'center',
            }}
          >          <Circle 
            sx={{ 
              fontSize: 10,
              color: getStatusColor(),
            }} 
          />
        </IconButton>
      </Tooltip>
      ) : (
        <Box sx={{ justifySelf: 'center' }} />
      )}

      {/* Right Side - Placeholder for future actions */}
      <Box sx={{ justifySelf: 'end' }} />
    </Box>
  );
};

export default ConnectionStatus;